import { useEffect, useRef, useState } from 'react';
import { Plus, X, Trash2, Clock } from 'lucide-react';
import { routineApi, strategiesApi, type Routine, type RoutineBlock, type RoutineItem } from '../../lib/api';
import { ICE, TEAL, ACCENT, FIELD, PANEL, LOSS, WIN, PX_FONT, TITLE_FONT, ON_ACCENT, pixelFrame } from '../guild/theme';

/* The trader's daily routine. Built in Readiness (Next steps): time blocks of prompts, each answered a
   certain way. Live Trading shows it as today's plan, where each prompt gets that day's answer. */

/** A New York clock time (the session the starter is built around) as the viewer's local HH:MM */
function nyToLocal(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const now = new Date();
  // Offset between New York and the viewer, today (handles both sides' daylight saving)
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const diffMin = Math.round((now.getTime() - ny.getTime()) / 60000);
  const total = (((h * 60 + m + diffMin) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

const note = (text: string): RoutineItem => ({ text, kind: 'note' });

/** Starter routine around the New York session, shown in each trader's own time zone */
export const starter = (): Routine => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
  blocks: [
    { name: 'Pre-session', start: nyToLocal('08:30'), end: nyToLocal('09:25'), items: [note('News on the calendar today'), note('Higher-timeframe levels and bias'), note('Lesson from yesterday’s journal')] },
    { name: 'Trading session', start: nyToLocal('09:30'), end: nyToLocal('11:30'), items: [
      { text: 'What strategy am I trading today?', kind: 'strategy' },
      { text: 'Max losses in a row this session', kind: 'number' },
      { text: 'Max total losses this session', kind: 'number' },
    ] },
    { name: 'Post-session', start: nyToLocal('11:30'), end: nyToLocal('12:00'), items: [note('One thing I did well'), note('One thing to fix'), { text: 'Discipline today (1–5)', kind: 'number' }] },
    // Planning is an evening habit wherever you are, so it stays at 8pm local
    { name: 'Next-day planning', start: '20:00', end: '20:30', items: [note('Tomorrow’s news events'), note('Levels to set alerts on')] },
  ],
});

/** Older routines stored items as plain strings */
const normalize = (r: Routine): Routine => ({
  ...r,
  blocks: r.blocks.map(b => ({ ...b, items: b.items.map(i => (typeof i === 'string' ? note(i) : i)) })),
});

const KIND_NAMES: Record<RoutineItem['kind'], string> = { note: 'Note', strategy: 'Strategy', number: 'Number' };
const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;
const input = { fontFamily: PX_FONT, fontSize: 13, color: ICE, background: FIELD, border: 'none', borderRadius: 'var(--pal-radius, 0)', boxShadow: 'var(--pal-field-shadow)', padding: '8px 10px', minWidth: 0 } as const;
const ghost = { ...label, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 } as const;

export function RoutineBuilder({ username, onClose, onSaved }: { username: string; onClose: () => void; onSaved: (r: Routine) => void }) {
  const [routine, setRoutine] = useState<Routine | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { routineApi.get(username).then(r => setRoutine(r.routine ? normalize(r.routine) : starter())).catch(() => setRoutine(starter())); }, [username]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const edit = (i: number, patch: Partial<RoutineBlock>) =>
    setRoutine(r => r && { ...r, blocks: r.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const editItem = (i: number, k: number, patch: Partial<RoutineItem>) =>
    setRoutine(r => r && { ...r, blocks: r.blocks.map((b, j) => (j === i ? { ...b, items: b.items.map((x, n) => (n === k ? { ...x, ...patch } : x)) } : b)) });
  const save = async () => {
    if (!routine) return;
    setBusy(true); setError(null);
    try { onSaved(await routineApi.save(username, routine)); onClose(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal aria-label="Build your trading routine" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'var(--pal-overlay)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: 'min(860px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: PANEL, boxShadow: pixelFrame(true), borderRadius: 'var(--pal-radius, 0)', padding: 22, display: 'flex', flexDirection: 'column', gap: 18, animation: 'pal-swap-in 0.3s ease-out both' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div className="pal-title" style={{ fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 28 }}>TRADING <span className="pal-em">ROUTINE</span></div>
            <div style={{ ...label, marginTop: 4 }}>
              Your day, block by block. Each prompt gets answered daily in Live Trading. Times are your local time ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} style={{ ...ghost, color: ICE }}><X size={18} /></button>
        </div>

        {!routine ? <div style={label}>Loading…</div> : routine.blocks.map((b, i) => (
          <div key={i} style={{ background: FIELD, borderRadius: 'var(--pal-radius, 0)', boxShadow: `inset 3px 0 0 ${ACCENT}`, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input aria-label="Block name" value={b.name} onChange={e => edit(i, { name: e.target.value })} style={{ ...input, flex: '1 1 200px', fontWeight: 700, background: PANEL }} />
              <Clock size={14} color={TEAL} />
              <input aria-label="Start" type="time" value={b.start ?? ''} onChange={e => edit(i, { start: e.target.value || null })} style={{ ...input, background: PANEL }} />
              <span style={label}>to</span>
              <input aria-label="End" type="time" value={b.end ?? ''} onChange={e => edit(i, { end: e.target.value || null })} style={{ ...input, background: PANEL }} />
              <button type="button" aria-label={`Remove ${b.name}`} onClick={() => setRoutine(r => r && { ...r, blocks: r.blocks.filter((_, j) => j !== i) })} style={{ ...ghost, color: LOSS }}><Trash2 size={14} /></button>
            </div>
            {b.items.map((item, k) => (
              <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: ACCENT, flex: '0 0 6px' }} />
                <input aria-label="Prompt" value={item.text} onChange={e => editItem(i, k, { text: e.target.value })} style={{ ...input, flex: 1, background: PANEL }} />
                <select aria-label="Answered as" value={item.kind} onChange={e => editItem(i, k, { kind: e.target.value as RoutineItem['kind'] })}
                  style={{ ...input, background: PANEL, width: 110 }}>
                  {(Object.keys(KIND_NAMES) as RoutineItem['kind'][]).map(kd => <option key={kd} value={kd}>{KIND_NAMES[kd]}</option>)}
                </select>
                <button type="button" aria-label="Remove prompt" onClick={() => edit(i, { items: b.items.filter((_, n) => n !== k) })} style={{ ...ghost }}><X size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => edit(i, { items: [...b.items, note('')] })} style={{ ...ghost, color: ACCENT, alignSelf: 'flex-start' }}><Plus size={13} /> Add prompt</button>
          </div>
        ))}

        {routine && (
          <button type="button" onClick={() => setRoutine(r => r && { ...r, blocks: [...r.blocks, { name: 'New block', start: null, end: null, items: [note('')] }] })}
            style={{ ...ghost, color: ACCENT, alignSelf: 'flex-start', fontSize: 11 }}><Plus size={14} /> Add a block</button>
        )}
        {error && <div style={{ ...label, color: LOSS }}>{error}</div>}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ ...label, padding: '10px 16px', border: 'none', cursor: 'pointer', background: FIELD, color: ICE, borderRadius: 'var(--pal-radius, 0)' }}>Cancel</button>
          <button type="button" disabled={busy || !routine} onClick={save}
            style={{ ...label, padding: '10px 18px', border: 'none', cursor: 'pointer', background: ACCENT, color: ON_ACCENT, fontWeight: 800, borderRadius: 'var(--pal-radius, 0)' }}>{busy ? 'Saving…' : 'Save routine'}</button>
        </div>
      </div>
    </div>
  );
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Today's plan in Live Trading: each prompt takes today's answer (saved as you type); the block in progress glows. */
export function RoutineView({ username, onEdit }: { username: string; onEdit: () => void }) {
  const [routine, setRoutine] = useState<Routine | null | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [strategies, setStrategies] = useState<string[]>([]);
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [now, setNow] = useState(() => new Date());
  const timer = useRef<number>();
  const day = today();
  useEffect(() => {
    routineApi.get(username).then(r => setRoutine(r.routine ? normalize(r.routine) : null)).catch(() => setRoutine(null));
    routineApi.day(username, day).then(setAnswers).catch(() => undefined);
    strategiesApi.list(username).then(l => setStrategies(l.map(s => s.code))).catch(() => undefined);
  }, [username, day]);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const answer = (key: string, value: string) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    setSaved('saving');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      routineApi.saveDay(username, day, next).then(() => setSaved('saved')).catch(() => setSaved('idle'));
    }, 600);
  };

  if (routine === undefined) return null;
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return (
    <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE }}>Today’s routine</span>
        <span style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {saved !== 'idle' && <span style={{ ...label, color: saved === 'saved' ? WIN : TEAL }}>{saved === 'saved' ? 'Saved' : 'Saving…'}</span>}
          <button type="button" onClick={onEdit} style={{ ...ghost, color: ACCENT }}>{routine ? 'Edit routine' : <><Plus size={13} /> Build your routine</>}</button>
        </span>
      </div>
      {!routine ? (
        <div style={label}>No routine yet. Build one so your day has a plan before the bell.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(routine.blocks.length, 4)}, minmax(0, 1fr))`, gap: 12 }} className="pal-board">
          {routine.blocks.map((b, i) => {
            const live = Boolean(b.start && b.end && hhmm >= b.start && hhmm < b.end);
            return (
              <div key={i} style={{ background: live ? 'var(--pal-tint-accent)' : FIELD, borderRadius: 'var(--pal-radius, 0)', padding: 12,
                boxShadow: live ? `inset 3px 0 0 ${ACCENT}, inset 0 0 0 1px ${ACCENT}88` : `inset 3px 0 0 ${ACCENT}55`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontFamily: PX_FONT, fontWeight: 800, fontSize: 13, color: ICE }}>{b.name}</span>
                  {b.start && <span style={{ ...label, color: live ? ACCENT : TEAL, whiteSpace: 'nowrap' }}>{b.start}{b.end ? `–${b.end}` : ''}</span>}
                </div>
                {live && <span className="pal-pulse" style={{ ...label, fontSize: 9, color: ACCENT }}>● Now</span>}
                {b.items.map((it, k) => {
                  const key = `${i}.${k}`;
                  const value = answers[key] ?? '';
                  return (
                    <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <span style={{ fontFamily: PX_FONT, fontSize: 12, lineHeight: 1.4, color: ICE }}>{it.text}</span>
                      {it.kind === 'strategy' ? (
                        <select value={value} onChange={e => answer(key, e.target.value)} style={{ ...input, background: PANEL }}>
                          <option value="">Pick…</option>
                          {strategies.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : it.kind === 'number' ? (
                        <input type="number" min={0} inputMode="numeric" value={value} onChange={e => answer(key, e.target.value)} style={{ ...input, background: PANEL, width: 90 }} />
                      ) : (
                        <textarea rows={2} value={value} onChange={e => answer(key, e.target.value)} placeholder="Today…"
                          style={{ ...input, background: PANEL, resize: 'vertical', fontSize: 12 }} />
                      )}
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
